/**
 * Sprint-related React hooks using direct database API.
 * Migrated from Firebase to /api/db/* endpoints in Phase 5.
 *
 * Note: Real-time subscription hooks have been removed.
 * Use the refetch() function to refresh data after mutations.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import type { DbSprint } from '@/lib/types/db-sprint'
import type { SprintIssueMapping } from '@/lib/types/db-issue-mapping'
import type { SprintMetrics } from '@/lib/types/db-sprint-metrics'
import { fetchWithAuthHandler, handleApiError } from '@/lib/api-error-handler'

// Request deduplication cache to prevent multiple simultaneous requests
const sprintCache = new Map<string, { data: DbSprint | null; timestamp: number }>()
const inFlightRequests = new Map<string, Promise<DbSprint>>()
const CACHE_TTL = 0 // Disabled cache to ensure fresh data for sprint details page

/**
 * Type for multi-project sprint queries (extends DbSprint with projectName)
 */
export interface DbSprintWithProject extends DbSprint {
  projectName?: string
}

/**
 * Convert UI status strings to database status numbers.
 */
function statusStringToNumber(status: string | undefined): number | undefined {
  if (!status) return undefined
  switch (status) {
    case 'open':
    case 'planned':
      return 0
    case 'active':
      return 1
    case 'closed':
    case 'completed':
      return 2
    default:
      return undefined
  }
}

/**
 * Hook to fetch and manage sprints for a project
 */
/**
 * Transform raw sprint data from backend (snake_case) to frontend format (camelCase)
 */
function transformSprint(raw: any): DbSprint {
  return {
    id: raw.id,
    projectId: raw.projectId ?? raw.project_id,
    projectName: raw.projectName ?? raw.project_name,
    projectIdentifier: raw.projectIdentifier ?? raw.project_identifier,
    name: raw.name,
    status: raw.status,
    startDate: raw.startDate ?? raw.start_date ?? null,
    endDate: raw.endDate ?? raw.end_date ?? null,
    sharing: raw.sharing ?? 0,
    createdAt: raw.createdAt ?? raw.created_at,
    updatedAt: raw.updatedAt ?? raw.updated_at,
  }
}

export function useSprints(projectId: number | null, filters?: { status?: string }) {
  const [sprints, setSprints] = useState<DbSprint[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!projectId) {
      setSprints([])
      return
    }

    setLoading(true)
    setError(null)

    const fetchSprints = async () => {
      try {
        const url = new URL('/api/db/sprints', window.location.origin)
        url.searchParams.set('projectIds', projectId.toString())
        // Add cache-busting timestamp
        url.searchParams.set('_t', Date.now().toString())

        const statusNum = statusStringToNumber(filters?.status)
        if (statusNum !== undefined) {
          url.searchParams.set('status', statusNum.toString())
        }

        const res = await fetchWithAuthHandler(url.toString(), {
          cache: 'no-store', // Prevent browser caching
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        })
        if (!res.ok) {
          throw new Error(`Failed to fetch sprints: ${res.status}`)
        }

        const data = await res.json()
        const { sprints } = data
        const transformed = sprints.map(transformSprint)
        setSprints(transformed)
      } catch (err) {
        handleApiError(err, () => {
          setError(err as Error)
        })
      } finally {
        setLoading(false)
      }
    }

    fetchSprints()
  }, [projectId, filters?.status])

  const refetch = useCallback(async () => {
    if (!projectId) return

    setLoading(true)
    setError(null)

    try {
      const url = new URL('/api/db/sprints', window.location.origin)
      url.searchParams.set('projectIds', projectId.toString())
      // Add cache-busting timestamp
      url.searchParams.set('_t', Date.now().toString())

      const statusNum = statusStringToNumber(filters?.status)
      if (statusNum !== undefined) {
        url.searchParams.set('status', statusNum.toString())
      }

      const res = await fetchWithAuthHandler(url.toString(), {
        cache: 'no-store', // Prevent browser caching
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      })
      if (!res.ok) {
        throw new Error(`Failed to fetch sprints: ${res.status}`)
      }

      const { sprints } = await res.json()
      setSprints(sprints.map(transformSprint))
    } catch (err) {
      handleApiError(err, () => {
        setError(err as Error)
      })
    } finally {
      setLoading(false)
    }
  }, [projectId, filters?.status])

  return { sprints, loading, error, refetch }
}

/**
 * Hook to fetch sprints from multiple projects (for hierarchical support)
 * Uses a SINGLE request with comma-separated project IDs for efficiency
 */
export function useSprintsFromMultipleProjects(
  projectIds: number[] | null,
  filters?: { status?: string },
  projectNames?: Record<number, string>
) {
  const [sprints, setSprints] = useState<DbSprintWithProject[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!projectIds || projectIds.length === 0) {
      setSprints([])
      return
    }

    setLoading(true)
    setError(null)

    const fetchSprints = async () => {
      try {
        const statusNum = statusStringToNumber(filters?.status)

        // Single request with all project IDs
        const url = new URL('/api/db/sprints', window.location.origin)
        url.searchParams.set('projectIds', projectIds.join(','))
        // Add cache-busting timestamp
        url.searchParams.set('_t', Date.now().toString())
        if (statusNum !== undefined) {
          url.searchParams.set('status', statusNum.toString())
        }

        const res = await fetchWithAuthHandler(url.toString(), {
          cache: 'no-store', // Prevent browser caching
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        })
        if (!res.ok) {
          throw new Error(`Failed to fetch sprints: ${res.status}`)
        }

        const { sprints: rawSprints } = await res.json()
        const allSprints = rawSprints.map((s: any) => ({
          ...transformSprint(s),
          projectName: s.projectName || s.project_name || projectNames?.[s.projectId || s.project_id]
        }))

        setSprints(allSprints)
      } catch (err) {
        handleApiError(err, () => {
          setError(err as Error)
        })
      } finally {
        setLoading(false)
      }
    }

    fetchSprints()
  }, [JSON.stringify(projectIds), filters?.status, JSON.stringify(projectNames)])

  const refetch = useCallback(async () => {
    if (!projectIds || projectIds.length === 0) return

    setLoading(true)
    setError(null)

    try {
      const statusNum = statusStringToNumber(filters?.status)

      // Single request with all project IDs
      const url = new URL('/api/db/sprints', window.location.origin)
      url.searchParams.set('projectIds', projectIds.join(','))
      // Add cache-busting timestamp
      url.searchParams.set('_t', Date.now().toString())
      if (statusNum !== undefined) {
        url.searchParams.set('status', statusNum.toString())
      }

      const res = await fetchWithAuthHandler(url.toString(), {
        cache: 'no-store', // Prevent browser caching
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      })
      if (!res.ok) {
        throw new Error(`Failed to fetch sprints: ${res.status}`)
      }

      const { sprints: rawSprints } = await res.json()
      const allSprints = rawSprints.map((s: any) => ({
        ...transformSprint(s),
        projectName: s.projectName || s.project_name || projectNames?.[s.projectId || s.project_id]
      }))

      setSprints(allSprints)
    } catch (err) {
      handleApiError(err, () => {
        setError(err as Error)
      })
    } finally {
      setLoading(false)
    }
  }, [projectIds, filters?.status, projectNames])

  return { sprints, loading, error, refetch }
}

/**
 * Hook to fetch a single sprint with request deduplication
 */
export function useSprint(projectId: number | null, sprintId: string | number | null) {
  const [sprint, setSprint] = useState<DbSprint | null>(null)
  const [loading, setLoading] = useState(!!sprintId)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    // Note: DB API doesn't need projectId (sprint ID is unique), but we keep param for compatibility
    if (sprintId === null || sprintId === undefined) {
      setSprint(null)
      setLoading(false)
      return
    }

    const numericSprintId = typeof sprintId === 'string' ? parseInt(sprintId, 10) : sprintId
    const cacheKey = `sprint-${numericSprintId}`

    setLoading(true)
    setError(null)

    const fetchSprint = async () => {
      try {
        // Check cache first
        const cached = sprintCache.get(cacheKey)
        if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
          setSprint(cached.data)
          setLoading(false)
          return
        }

        // Check if there's already an in-flight request
        let requestPromise = inFlightRequests.get(cacheKey)

        if (!requestPromise) {
          const url = `/api/db/sprints/${numericSprintId}`

          requestPromise = fetchWithAuthHandler(url, { cache: 'no-store' }).then(async (res) => {
            if (!res.ok) {
              const errorText = await res.text()
              let errorData
              try {
                errorData = JSON.parse(errorText)
              } catch {
                errorData = { message: errorText }
              }

              // Show clear error message in console
              console.error('[useSprint] Failed to load sprint', numericSprintId, ':', {
                status: res.status,
                error: errorData.error || errorData.message,
                details: errorData.details
              })

              throw new Error(errorData.error || errorData.message || `Failed to fetch sprint (${res.status})`)
            }

            const data = await res.json()
            const { sprint } = data

            if (!sprint) {
              console.error('[useSprint] No sprint data in response')
              throw new Error('No sprint data returned from API')
            }

            // Cache the result
            sprintCache.set(cacheKey, { data: sprint, timestamp: Date.now() })

            return sprint
          }).finally(() => {
            // Clean up in-flight request
            inFlightRequests.delete(cacheKey)
          })

          inFlightRequests.set(cacheKey, requestPromise)
        }

        const sprintData = await requestPromise
        setSprint(sprintData)
      } catch (err) {
        console.error('[useSprint] Error:', err instanceof Error ? err.message : err)
        handleApiError(err, () => {
          setError(err as Error)
        })
      } finally {
        setLoading(false)
      }
    }

    fetchSprint()
  }, [projectId, sprintId])

  const refetch = useCallback(async () => {
    if (!sprintId) return

    const numericSprintId = typeof sprintId === 'string' ? parseInt(sprintId, 10) : sprintId
    const cacheKey = `sprint-${numericSprintId}`

    console.log('[useSprint] Refetching sprint:', numericSprintId)

    // Clear cache on manual refetch
    sprintCache.delete(cacheKey)
    inFlightRequests.delete(cacheKey)

    setLoading(true)
    setError(null)

    try {
      // Add cache-busting timestamp to ensure fresh data from backend
      const url = `/api/db/sprints/${numericSprintId}?_t=${Date.now()}`
      console.log('[useSprint refetch] Making API request to:', url)

      const res = await fetchWithAuthHandler(url, {
        cache: 'no-store', // Prevent browser caching
      })
      console.log('[useSprint refetch] API response status:', res.status, res.ok)

      if (!res.ok) {
        const errorText = await res.text()
        console.error('[useSprint refetch] API error response:', errorText)
        throw new Error(`Failed to fetch sprint: ${res.status} - ${errorText}`)
      }

      const { sprint } = await res.json()
      console.log('[useSprint refetch] API response sprint:', sprint)
      console.log('[useSprint refetch] Sprint issues count:', sprint?.issues?.length || 0)
      console.log('[useSprint refetch] Sprint issue IDs:', sprint?.issues?.map((i: any) => i.id) || [])

      if (!sprint) {
        console.error('[useSprint refetch] No sprint in response data')
        throw new Error('No sprint data returned from API')
      }

      // Update cache
      sprintCache.set(cacheKey, { data: sprint, timestamp: Date.now() })

      setSprint(sprint)
      console.log('[useSprint refetch] Sprint updated successfully:', numericSprintId, 'with', sprint.issues?.length || 0, 'issues')
    } catch (err) {
      console.error('[useSprint refetch] Error:', err)
      handleApiError(err, () => {
        setError(err as Error)
      })
    } finally {
      setLoading(false)
    }
  }, [sprintId])

  return { sprint, loading, error, refetch }
}

/**
 * Hook to fetch issues for a sprint
 * Note: Uses sprint details endpoint which already includes issues array
 */
export function useSprintIssues(projectId: number | null, sprintId: string | number | null) {
  const { sprint, loading, error, refetch: refetchSprint } = useSprint(projectId, sprintId)

  const issues = useMemo(() => {
    // Sprint details endpoint returns issues array embedded in sprint object
    return (sprint as any)?.issues || []
  }, [sprint])

  return { issues, loading, error, refetch: refetchSprint }
}

/**
 * Hook to fetch issues for a sprint from multiple projects (for hierarchy support)
 * Used when viewing a parent project sprint that may contain tasks from child projects
 */
export function useSprintIssuesFromMultipleProjects(
  projectIds: number[] | null,
  sprintId: string | number | null
) {
  // Simply delegate to useSprintIssues - the DB already returns issues from any project
  // that are assigned to this sprint (cross-project via sharing)
  const numericSprintId = typeof sprintId === 'string' ? sprintId : sprintId?.toString() ?? null
  return useSprintIssues(null, numericSprintId)
}

/**
 * Hook to get the active sprint for a project
 */
export function useActiveSprint(projectId: number | null) {
  const { sprints, loading, error, refetch } = useSprints(projectId, { status: 'active' })

  return {
    activeSprint: sprints.length > 0 ? sprints[0] : null,
    loading,
    error,
    refetch
  }
}

/**
 * Hook to get sprint assignment for a specific issue
 */
export function useIssueSprintMapping(projectId: number | null, issueId: number | null) {
  const [mapping, setMapping] = useState<{ sprintId: number; storyPoints: number | null } | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    // Note: DB API doesn't need projectId, but we keep param for compatibility
    if (!issueId) {
      setMapping(null)
      return
    }

    setLoading(true)
    setError(null)

    const fetchMapping = async () => {
      try {
        // Add cache-busting timestamp to ensure fresh data
        const url = `/api/db/issues/${issueId}?_t=${Date.now()}`
        const res = await fetchWithAuthHandler(url, {
          cache: 'no-store', // Prevent browser caching
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        })

        // Handle 404 as no mapping (not an error)
        if (res.status === 404) {
          setMapping(null)
          setLoading(false)
          return
        }

        if (!res.ok) {
          throw new Error(`Failed to fetch issue sprint mapping: ${res.status}`)
        }

        const data = await res.json()
        // API returns { sprintId, storyPoints } - convert to mapping format
        if (data.sprintId !== null) {
          setMapping({ sprintId: data.sprintId, storyPoints: data.storyPoints })
        } else {
          setMapping(null)
        }
      } catch (err) {
        handleApiError(err, () => {
          setError(err as Error)
        })
      } finally {
        setLoading(false)
      }
    }

    fetchMapping()
  }, [projectId, issueId])

  const refetch = useCallback(async () => {
    if (!issueId) return

    setLoading(true)
    setError(null)

    try {
      // Add cache-busting timestamp to ensure fresh data
      const url = `/api/db/issues/${issueId}?_t=${Date.now()}`
      const res = await fetchWithAuthHandler(url, {
        cache: 'no-store', // Prevent browser caching
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      })

      if (res.status === 404) {
        setMapping(null)
        setLoading(false)
        return
      }

      if (!res.ok) {
        throw new Error(`Failed to fetch issue sprint mapping: ${res.status}`)
      }

      const data = await res.json()
      if (data.sprintId !== null) {
        setMapping({ sprintId: data.sprintId, storyPoints: data.storyPoints })
      } else {
        setMapping(null)
      }
    } catch (err) {
      handleApiError(err, () => {
        setError(err as Error)
      })
    } finally {
      setLoading(false)
    }
  }, [issueId])

  return { mapping, loading, error, refetch }
}

/**
 * Hook to get sprint metrics
 * Note: Calculates metrics from sprint data on the client side
 */
export function useSprintMetrics(projectId: number | null, sprintId: string | number | null) {
  const { sprint, loading, error, refetch } = useSprint(projectId, sprintId)

  const metrics = useMemo(() => {
    // Sprint details endpoint returns issues array embedded in sprint object
    const sprintWithIssues = sprint as any
    if (!sprintWithIssues || !sprintWithIssues.issues) {
      return null
    }

    const issues = sprintWithIssues.issues
    const totalIssues = issues.length

    if (totalIssues === 0) {
      return {
        points: {
          total: 0,
          completed: 0
        },
        issues: {
          total: 0,
          completed: 0,
          inProgress: 0
        },
        burndown: []
      }
    }

    // Count issues by status - use status.is_closed field (most reliable)
    // This matches the sprint details stats calculation
    const completedIssues = issues.filter((issue: any) => {
      return issue.status?.is_closed === true || issue.status?.is_closed === 1
    }).length

    const inProgressIssues = issues.filter((issue: any) =>
      !(issue.status?.is_closed === true || issue.status?.is_closed === 1) && issue.doneRatio > 0
    ).length

    // Calculate story points (story_points field from agile_data ONLY - no fallback to estimated_hours)
    // This matches the sprint details stats calculation
    const totalPoints = issues.reduce((sum: number, issue: any) =>
      sum + (issue.storyPoints || 0), 0
    )

    const completedPoints = issues
      .filter((issue: any) => issue.status?.is_closed === true || issue.status?.is_closed === 1)
      .reduce((sum: number, issue: any) => sum + (issue.storyPoints || 0), 0)

    // Calculate in-progress points (issues with doneRatio > 0 but not closed)
    const inProgressPoints = issues
      .filter((issue: any) => !(issue.status?.is_closed === true || issue.status?.is_closed === 1) && issue.doneRatio > 0)
      .reduce((sum: number, issue: any) => sum + (issue.storyPoints || 0), 0)

    // Generate burndown data based on sprint timeline
    const burndown = []
    if (sprintWithIssues.startDate && sprintWithIssues.endDate) {
      const startDate = new Date(sprintWithIssues.startDate)
      startDate.setHours(0, 0, 0, 0)
      const endDate = new Date(sprintWithIssues.endDate)
      endDate.setHours(23, 59, 59, 999)
      const today = new Date()
      today.setHours(23, 59, 59, 999)

      // Calculate total sprint days
      const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
      const daysElapsed = Math.min(
        totalDays,
        Math.max(0, Math.ceil((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)))
      )

      // Build a map of cumulative completed points by day based on closed_on dates
      const completedByDay: number[] = new Array(totalDays + 1).fill(0)

      // Calculate completed points for each day based on actual closure dates
      issues.forEach((issue: any) => {
        if (issue.status?.is_closed === true || issue.status?.is_closed === 1) {
          const issuePoints = issue.storyPoints || 0  // Only use storyPoints, no fallback
          const closedOn = issue.closedOn || issue.closed_on

          if (closedOn) {
            const closedDate = new Date(closedOn)
            closedDate.setHours(0, 0, 0, 0)
            const dayIndex = Math.ceil((closedDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))

            // Add points to the day it was closed (clamped to sprint range)
            if (dayIndex >= 0 && dayIndex <= totalDays) {
              completedByDay[dayIndex] += issuePoints
            } else if (dayIndex < 0) {
              // Closed before sprint started - count as day 0
              completedByDay[0] += issuePoints
            } else {
              // Closed after sprint ended - count as last day
              completedByDay[totalDays] += issuePoints
            }
          } else {
            // No closed_on date - distribute to current day
            const currentDayIndex = Math.min(daysElapsed, totalDays)
            completedByDay[currentDayIndex] += issuePoints
          }
        }
      })

      // Convert to cumulative completed points
      let cumulativeCompleted = 0
      const cumulativeByDay: number[] = []
      for (let day = 0; day <= totalDays; day++) {
        cumulativeCompleted += completedByDay[day]
        cumulativeByDay.push(cumulativeCompleted)
      }

      // Generate data points for each day
      for (let day = 0; day <= totalDays; day++) {
        const isProjected = day > daysElapsed

        // For past/current days, use actual cumulative completed points
        // For projected days, extrapolate from current velocity
        let dayCompletedPoints: number
        if (isProjected) {
          // Project completion based on average velocity so far
          const avgVelocity = daysElapsed > 0 ? cumulativeByDay[daysElapsed] / daysElapsed : 0
          dayCompletedPoints = Math.min(totalPoints, cumulativeByDay[daysElapsed] + avgVelocity * (day - daysElapsed))
        } else {
          dayCompletedPoints = cumulativeByDay[day]
        }

        // Remaining points = total - completed
        // In-progress points: only available for current day (we don't have historical in-progress data)
        const remainingPoints = totalPoints - dayCompletedPoints
        const dayInProgressPoints = (day === daysElapsed) ? inProgressPoints : 0

        burndown.push({
          day,
          date: new Date(startDate.getTime() + day * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          remainingPoints: Math.max(0, Math.round(remainingPoints - dayInProgressPoints)), // Exclude in-progress from remaining
          completedPoints: Math.round(dayCompletedPoints),
          inProgressPoints: dayInProgressPoints,
          totalPoints,
          isProjected
        })
      }
    }

    return {
      points: {
        total: totalPoints,
        completed: completedPoints,
        inProgress: inProgressPoints
      },
      issues: {
        total: totalIssues,
        completed: completedIssues,
        inProgress: inProgressIssues
      },
      burndown
    }
  }, [sprint])

  return { metrics, loading, error, refetch }
}
